<template>
  <div class="derivatives" data-testid="derivatives">
    <DerivativesFilters
      class="derivatives__filters"
      :filters="filters"
      :updated-at="updatedAtMessage"
      :is-loading="isLoading && !updatedAtMessage"
      @on-filter-change="handleOnSelectFilter"
    />
    <template v-if="hasError">
      <HelperViewStatus
        class="derivatives__error"
        title="Não foi possível carregar os seus ativos"
        description="Aguarde um momento e tente novamente"
        button-label="Tentar novamente"
        status="alert"
        @click="getDerivativesData"
      />
    </template>
    <template v-else>
      <DerivativesTable
        :filters="filters"
        :is-loading="isLoading"
        :items-per-page="pagination.itemsPerPage"
        :items="items"
      />
      <Pagination
        class="derivatives__pagination"
        data-testid="derivatives-pagination"
        :is-loading="isLoading"
        :total-pages="pagination.totalPages"
        :current-page="pagination.currentPage"
        @page-changed="handleOnPageChange"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted } from 'vue';

import { DerivativesFilters } from './components/DerivativesFilters';
import { DerivativesTable } from './components/DerivativesTable';
import { HelperViewStatus } from '@warrenbrasil/nebraska-web';
import { Pagination } from '@/modules/common/components/Pagination';

import { isRequestError } from '@warrenbrasil/web-http';
import {
  IDerivativesFilters,
  DerivativesFiltersTypes,
  DerivativesFiltersTypeValues,
  DerivativesFiltersDateValues
} from './types';
import { PositionsDateFilterOptions, PositionsTypeFilterOptions } from './data';
import {
  getOptionsList,
  GetOptionsListResponse,
  OptionPosition,
  getHistoricalOptionsList,
  GetHistoricalOptionsListResponse,
  HistoricalOptionPosition,
  getForwardList,
  GetForwardListResponse,
  ForwardContractPosition
} from '@/modules/trade/services/instruments';

const items = ref<
  OptionPosition[] | ForwardContractPosition[] | HistoricalOptionPosition[]
>([]);
const isLoading = ref<boolean>(false);
const hasError = ref<boolean>(false);
const updatedAtMessage = ref<string>('');
const abortSignal = ref<AbortController | null>(null);
const pagination = ref({
  currentPage: 1,
  totalPages: 1,
  itemsPerPage: 10
});
const filters: IDerivativesFilters = reactive({
  [DerivativesFiltersTypes.Date]: {
    selected: DerivativesFiltersDateValues.Today,
    available: PositionsDateFilterOptions
  },
  [DerivativesFiltersTypes.Type]: {
    selected: DerivativesFiltersTypeValues.Options,
    available: PositionsTypeFilterOptions
  }
});

const servicesByFilter = {
  [DerivativesFiltersTypeValues.Options]: {
    [DerivativesFiltersDateValues.Today]: getOptionsList,
    [DerivativesFiltersDateValues.Historical]: getHistoricalOptionsList
  },
  [DerivativesFiltersTypeValues.Forwards]: {
    [DerivativesFiltersDateValues.Today]: getForwardList,
    [DerivativesFiltersDateValues.Historical]: () => {}
  }
};

const mountView = (
  data:
    | GetOptionsListResponse
    | GetForwardListResponse
    | GetHistoricalOptionsListResponse
    | void
) => {
  if (!data) return;
  const {
    updatedAt,
    items: derivativesItems,
    pagination: { itemsPerPage, totalPages, page }
  } = data;
  updatedAtMessage.value = updatedAt.formatted;
  items.value = derivativesItems;
  pagination.value = {
    totalPages,
    itemsPerPage,
    currentPage: page
  };
};

const getDerivativesData = async () => {
  try {
    isLoading.value = true;
    hasError.value = false;
    if (abortSignal.value) abortSignal.value.abort();
    abortSignal.value = new AbortController();

    const selectedType =
      filters[DerivativesFiltersTypes.Type].selected ||
      DerivativesFiltersTypeValues.Options;
    const selectedDate =
      filters[DerivativesFiltersTypes.Date].selected ||
      DerivativesFiltersDateValues.Today;

    const service = servicesByFilter[selectedType][selectedDate];

    const data = await service(
      {
        itemsPerPage: pagination.value.itemsPerPage,
        page: pagination.value.currentPage
      },
      abortSignal.value.signal
    );

    mountView(data);
  } catch (e) {
    if (isRequestError(e) && e.type === 'canceled') return;
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const handleOnSelectFilter = (
  filterType: DerivativesFiltersTypes,
  filterValue: DerivativesFiltersTypeValues | DerivativesFiltersDateValues
) => {
  if (filterType === DerivativesFiltersTypes.Type) {
    filters[DerivativesFiltersTypes.Date].selected =
      DerivativesFiltersDateValues.Today;
  }
  filters[filterType].selected = filterValue;
  pagination.value.currentPage = 1;
  getDerivativesData();
};

const handleOnPageChange = (page: number) => {
  pagination.value.currentPage = page;
  getDerivativesData();
};
onMounted(() => getDerivativesData());
</script>
<style lang="less" scoped>
.derivatives {
  padding: @size-spacing-x600 @size-spacing-x500 0 @size-spacing-x500;

  .to-lg({ padding: @size-spacing-x400 @size-spacing-x400;});

  &__filters {
    margin-bottom: @size-spacing-x500;
  }

  &__pagination {
    margin: @size-spacing-x500 0;
  }

  &__error {
    margin: @size-spacing-x1800 auto @size-spacing-x400 auto;
  }
}
</style>
